<script lang="ts">
  // FinishedModal — aparece quando a partida termina. Mostra verdict
  // (VITORIA/DERROTA/EMPATE) + placar final. Botao volta ao lobby.
  import type { MatchSnapshot } from '../../stores/match';
  import { matchSnapshot } from '../../stores/match';
  import { userId, lastRankingDelta } from '../../stores/auth';

  type Props = { snap: MatchSnapshot };
  let { snap }: Props = $props();

  // Delay antes do modal aparecer pra player ver a ultima carta na mesa.
  // Leonardo 2026-05-21: "no final de tudo, p ver a ultima carta".
  // Quando status vira 'finished', espera 2.5s antes de exibir.
  const REVEAL_DELAY_MS = 2500;
  let visible = $state(false);
  let timer: number | null = null;
  $effect(() => {
    if (snap.status === 'finished' && !visible && timer === null) {
      timer = window.setTimeout(() => {
        visible = true;
        timer = null;
      }, REVEAL_DELAY_MS);
    } else if (snap.status !== 'finished') {
      visible = false;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    }
  });

  // Derivar won/draw conforme format. Em 2v2 snap.winner = team leader,
  // entao comparamos via team membership.
  let mineIds = $derived.by((): string[] => {
    if (snap.format === '2v2') {
      const a = (snap.team_a || []).map((p) => p.id);
      const b = (snap.team_b || []).map((p) => p.id);
      return a.includes($userId) ? a : b.includes($userId) ? b : [];
    }
    return [$userId];
  });

  let won = $derived(!!snap.winner && mineIds.includes(snap.winner));
  let draw = $derived(!snap.winner);

  // Score do meu lado vs oposto. Em 2v2 usa team_a/b scores; em 1v1
  // p1/p2 scores.
  let myScore = $derived.by(() => {
    if (snap.format === '2v2') {
      const myTeam = snap.team_a?.some((p) => p.id === $userId) ? 'a' : 'b';
      return myTeam === 'a' ? (snap.team_a_score ?? 0) : (snap.team_b_score ?? 0);
    }
    return snap.p1?.id === $userId ? (snap.p1.game_score ?? 0) : (snap.p2.game_score ?? 0);
  });
  let oppScore = $derived.by(() => {
    if (snap.format === '2v2') {
      const myTeam = snap.team_a?.some((p) => p.id === $userId) ? 'a' : 'b';
      return myTeam === 'a' ? (snap.team_b_score ?? 0) : (snap.team_a_score ?? 0);
    }
    return snap.p1?.id === $userId ? (snap.p2.game_score ?? 0) : (snap.p1.game_score ?? 0);
  });

  let verdict = $derived(won ? 'VITÓRIA' : draw ? 'EMPATE' : 'DERROTA');
  let plural = $derived(snap.format === '2v2');
  let bigToast = $derived.by(() => {
    if (draw) return null;
    const subj = plural ? 'VOCÊS' : 'VOCÊ';
    const verb = plural ? (won ? 'VENCERAM' : 'PERDERAM') : (won ? 'VENCEU' : 'PERDEU');
    return `${subj} ${verb} A PARTIDA`;
  });

  // Delta de ranking (+10/-10) vindo do evento ranking:update — so existe
  // em partida de torneio. null em partida avulsa (nao mexe ranking).
  let rankDelta = $derived($lastRankingDelta?.delta ?? null);

  function backToLobby() {
    matchSnapshot.set(null);
    lastRankingDelta.set(null);
  }
</script>

{#if visible}
  <div class="overlay">
    <div class="card" class:defeat={!won && !draw} class:draw>
      <div class="emblem">
        {#if won}🏆{:else if draw}≈{:else}💀{/if}
      </div>
      {#if bigToast}
        <p class="big-toast">{bigToast}</p>
      {/if}
      <div class="eyebrow">::: fim de missão</div>
      <h2 class="verdict">{verdict}</h2>

      <div class="score-viz">
        <div class="side me">
          <span class="lbl">você</span>
          <span class="val">{myScore}</span>
        </div>
        <div class="vs">×</div>
        <div class="side opp">
          <span class="lbl">{plural ? 'eles' : 'oponente'}</span>
          <span class="val">{oppScore}</span>
        </div>
      </div>

      {#if rankDelta !== null}
        <div class="rank-delta" class:up={rankDelta >= 0} class:down={rankDelta < 0}>
          <span class="rank-arrow">{rankDelta >= 0 ? '▲' : '▼'}</span>
          <span class="rank-val">{rankDelta >= 0 ? '+' : ''}{rankDelta}</span>
          <span class="rank-lbl">RANKING</span>
        </div>
      {/if}

      <button class="back-btn" onclick={backToLobby}>voltar ao lobby</button>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(2, 3, 26, 0.88);
    backdrop-filter: blur(8px);
    animation: fade 0.25s ease-out;
    overflow-y: auto;
  }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }

  .card {
    width: 100%;
    max-width: 360px;
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
    padding: clamp(16px, 4vh, 28px) 20px;
    background: linear-gradient(180deg, var(--bg-elev), rgba(2, 3, 26, 0.95));
    border: 1px solid rgba(108, 255, 189, 0.5);
    border-radius: var(--radius-lg);
    box-shadow:
      0 18px 50px rgba(0, 0, 0, 0.7),
      0 0 36px rgba(108, 255, 189, 0.3);
    text-align: center;
    animation: pop 0.35s cubic-bezier(0.2, 1.4, 0.4, 1);
  }
  @keyframes pop {
    0% { transform: scale(0.7); opacity: 0; }
    60% { transform: scale(1.05); }
    100% { transform: scale(1); opacity: 1; }
  }
  .card.defeat {
    border-color: rgba(255, 76, 126, 0.5);
    box-shadow:
      0 18px 50px rgba(0, 0, 0, 0.7),
      0 0 36px rgba(255, 76, 126, 0.25);
  }
  .card.draw {
    border-color: rgba(255, 210, 74, 0.5);
    box-shadow:
      0 18px 50px rgba(0, 0, 0, 0.7),
      0 0 36px rgba(255, 210, 74, 0.2);
  }

  .emblem {
    font-size: clamp(36px, 7vh, 54px);
    line-height: 1;
    margin-bottom: 8px;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }
  .big-toast {
    margin: 0 0 6px;
    font-size: clamp(14px, 2.5vh, 18px);
    font-weight: 900;
    letter-spacing: 1.5px;
    color: var(--text);
  }
  .eyebrow {
    font-size: 10px;
    letter-spacing: 2px;
    color: var(--text-dim);
    text-transform: uppercase;
  }
  .verdict {
    margin: 4px 0 14px;
    font-size: clamp(22px, 4.5vh, 32px);
    letter-spacing: 4px;
    color: var(--ok);
    font-weight: 900;
  }
  .card.defeat .verdict { color: var(--danger); }
  .card.draw .verdict { color: var(--warn); }

  .score-viz {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    margin-bottom: clamp(12px, 2.5vh, 22px);
  }
  .side {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 70px;
  }
  .side .lbl {
    font-size: 10px;
    color: var(--text-dim);
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  .side .val {
    font-size: clamp(26px, 5vh, 36px);
    font-weight: 900;
    line-height: 1;
  }
  .side.me .val { color: var(--ok); }
  .side.opp .val { color: var(--text); }
  .vs {
    font-size: 24px;
    color: var(--text-dim);
  }

  /* Delta de ranking (+10/-10) — so em partida de torneio. */
  .rank-delta {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    margin: 0 auto clamp(12px, 2.5vh, 20px);
    padding: 8px 18px;
    border-radius: 999px;
    font-weight: 900;
    animation: rank-pop 0.5s cubic-bezier(0.2, 1.5, 0.4, 1) 0.15s both;
  }
  .rank-delta.up {
    background: rgba(76, 175, 80, 0.16);
    border: 2px solid rgba(76, 175, 80, 0.55);
    color: #6cffbd;
  }
  .rank-delta.down {
    background: rgba(255, 76, 126, 0.14);
    border: 2px solid rgba(255, 76, 126, 0.5);
    color: #ff7da2;
  }
  .rank-arrow { font-size: 13px; }
  .rank-val { font-size: clamp(20px, 3.5vh, 26px); letter-spacing: 1px; }
  .rank-lbl {
    font-size: 10px;
    letter-spacing: 2px;
    opacity: 0.85;
    align-self: center;
  }
  @keyframes rank-pop {
    0% { transform: scale(0.4) translateY(8px); opacity: 0; }
    60% { transform: scale(1.12); }
    100% { transform: scale(1) translateY(0); opacity: 1; }
  }

  .back-btn {
    width: 100%;
    padding: 12px;
    background: var(--ok);
    color: #001a10;
    border: none;
    font-weight: 800;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  .back-btn:hover { background: #5af0a8; }
</style>
